import {
	AfterViewInit,
	Component,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
	BasePageComponent,
	PageSkeletonType,
} from '@app/abstracts/base-page/base-page.component';
import {
	Formation,
	FormationAdministrativeStatus,
	FormationService,
	FormationAdministrativeStatusValues,
} from '@app/models/formation';
import {
	FormationSession,
	FormationSessionService,
} from '@app/models/formation-session';
import {
	FormationIntervention,
	FormationInterventionSearchParams,
} from '@app/models/formation-intervention';
import { FormationInterventionWalkerService } from '@app/models/formation-intervention/formation-intervention-walker.service';
import { AttestationPresenceService } from '@app/models/attestation-presence/attestation-presence.service';
import { User } from '@app/models/user/user';
import { NzModalService } from 'ng-zorro-antd/modal';

import { PageInterface, PageService } from '@app/services/page.service';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ErrorService } from '@app/services/error.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { HttpClient } from '@angular/common/http';
import { FormationTheme } from '@app/models/formation-theme';

@Component({
	selector: 'app-molecule-formation-session-details-admin',
	templateUrl: './molecule-formation-session-details-admin.component.html',
	styleUrls: ['./molecule-formation-session-details-admin.component.less'],
})
export class MoleculeFormationSessionDetailsAdminComponent
	extends BasePageComponent
	implements OnInit, OnDestroy, AfterViewInit
{
	@ViewChild('attestationPresenceTable') attestationPresenceTable: any;

	protected skeleton: PageSkeletonType = 'list';
	private unsubscribe$ = new Subject<void>();
	searchParams: FormationInterventionSearchParams;
	formation: Formation;
	formationSession: FormationSession;
	formationInterventions: FormationIntervention[];
	numberIntervention: number;
	formationId: string;
	sessionId: string;

	// Attestation de présence properties
	respondants: User[] = [];
	attestationTemplateOptions: string[] = [
		'attestation-presence-fae-ach',
		'attestation-presence-fae-ama',
		'attestation-presence-fae-th-tsh',
	];
	attestationPresenceLoading = false;
	defaultTemplate: string | null = null;

	sorts = ['created_at', 'label'];
	orders = ['asc', 'desc'];
	loading = false;

	statesAdmin = FormationAdministrativeStatusValues;

	constructor(
		protected pageService: PageService,
		protected translate: TranslateService,
		public formationInterventionWalkerService: FormationInterventionWalkerService,
		public formationService: FormationService,
		public formationSessionService: FormationSessionService,
		private attestationPresenceService: AttestationPresenceService,
		private modalService: NzModalService,
		private route: ActivatedRoute,
		private errorService: ErrorService,
		private messageConfirmation: NzMessageService,
		private http: HttpClient
	) {
		super(pageService, translate);
	}

	async ngOnInit() {
		super.ngOnInit();
		this.loading = true;
		this.formationId = this.route.snapshot.params.id;
		this.sessionId = this.route.snapshot.params.sessionId;

		try {
			// Get formation data with sponsor and avatar
			this.formationService
				.getAsAdmin(this.formationId, {
					_populate: ['sponsor.avatar', 'respondants', 'themes'],
				})
				.then((formation) => {
					this.formation = formation;
					// Set respondants from formation
					if (formation && formation.respondantsExists()) {
						this.respondants = formation.props
							.respondants as User[];
					}

					this.defaultTemplate = this.getDefaultTemplate(formation);
				});

			// Get session data
			this.formationSessionService
				.getAsAdmin(this.sessionId, {
					_populate: [
						'interventions.thematiques',
						'interventions.formateur',
					],
				})
				.then((formationSession) => {
					this.formationSession = formationSession;
					this.getFormationInterventions();
				});

			this.refreshPageData();
		} catch (error) {
			console.error('Error loading data:', error);
		} finally {
			this.loading = false;
		}
	}

	getDefaultTemplate(formation: Formation): string | null {
		if (formation && formation.themesExists()) {
			const themes = formation.props.themes as FormationTheme[];
			if (themes[0]?.getLabel()) {
				return `attestation-presence-${themes[0]
					.getLabel()
					.toLowerCase()
					.replace(/ /g, '-')}`;
			}
		}
		return null;
	}

	ngAfterViewInit() {
		super.ngAfterViewInit();
	}

	ngOnDestroy() {
		super.ngOnDestroy();
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	getFormationInterventions() {
		this.loading = true;

		this.formationInterventions = this.formationSession.props
			.interventions as FormationIntervention[];
		this.numberIntervention = this.formationInterventions.length;

		this.loading = false;
	}

	trackById(_: number, intervention: FormationIntervention): string {
		return intervention.getId();
	}

	getPageData(): PageInterface {
		return {
			title: this.translate.instant(
				'page_title-formation_session_interventions',
				{
					formation: this.formation ? this.formation.getLabel() : '',
					session: this.formationSession
						? this.formationSession.getLabel()
						: '',
				}
			),
			breadcrumbs: [
				{
					label: this.translate.instant('top_bar-formation_list'),
					link: ['/formations/list'],
				},
				{
					label: this.formation ? this.formation.getLabel() : null,
					link: ['/formations', this.formationId, 'details'],
				},
				{
					label: this.formationSession?.getLabel(),
				},
			],
			hideTopBar: false,
			hideMenu: false,
		};
	}

	getLastDateFromFormationInterventions(
		formationInterventions: FormationIntervention[]
	) {
		if (!formationInterventions.length) {
			return 0;
		}
		return Math.max(
			...formationInterventions.map((formationIntervention) =>
				formationIntervention.getEndDate()
			)
		);
	}

	handleFilterState(value: FormationAdministrativeStatus) {
		if (value) {
			this.searchParams.props.administrative_status = value;
		} else {
			delete this.searchParams.props.administrative_status;
		}
		this.searchParams.next();
	}

	// Attestation de présence methods
	isGenerateDisable(docType: string): boolean {
		return docType === 'attestation_presence' ? false : true;
	}

	async handleGenerate(event: any, docType: string): Promise<void> {
		if (docType !== 'attestation_presence') return;

		try {
			this.attestationPresenceLoading = true;

			// For attestation presence, we need to generate one for each respondant
			if (this.respondants && this.respondants.length > 0) {
				// Use respondant-specific time overrides from the event
				const respondantTimeOverrides =
					event.respondantTimeOverrides || {};

				// Process respondants sequentially to avoid overwhelming the server
				await Promise.all(
					this.respondants.map((respondant) => {
						const timeOverride =
							respondantTimeOverrides[respondant.props._id];
						return this.attestationPresenceService.generateAttestationPresence(
							this.formationSession.props._id,
							respondant.props._id,
							event.template,
							timeOverride?.toString()
						);
					})
				);

				await this.attestationPresenceTable.refresh();
			}
		} catch (error) {
			console.error('Error generating attestation presence:', error);
		} finally {
			this.attestationPresenceLoading = false;
			if (this.attestationPresenceTable) {
				this.attestationPresenceTable.refresh();
			}
		}
	}

	async handleGenerateForRespondant(event: any): Promise<void> {
		if (!event.respondantId || !this.sessionId) return;

		try {
			this.attestationPresenceLoading = true;

			await this.attestationPresenceService.generateAttestationPresence(
				this.sessionId,
				event.respondantId,
				event.template,
				event.totalTimeOverride?.toString()
			);
		} catch (error) {
			console.error(
				'Error generating attestation for respondant:',
				error
			);
		} finally {
			this.attestationPresenceLoading = false;
			if (this.attestationPresenceTable) {
				this.attestationPresenceTable.refresh();
			}
		}
	}

	async handleReadDocumentAttestationPresence(id: string) {
		try {
			const { url } =
				await this.formationSessionService.getPresignedAttestationPresenceAsAdmin(
					this.formationSession.props._id,
					id
				);
			window.open(url, '_blank');
		} catch (error) {
			this.errorService.handle(error);
		}
	}

	async handleDownloadDocumentAttestationPresence(event: {
		attestationId: string;
		filename: string;
	}) {
		try {
			const { url } =
				await this.formationSessionService.getPresignedAttestationPresenceAsAdmin(
					this.formationSession.props._id,
					event.attestationId
				);

			this.downloadPdf(url, event.filename);
		} catch (error) {
			this.errorService.handle(error);
		}
	}

	async handleSendAttestationPresence(attestationId: string) {
		try {
			await this.formationSessionService.sendEmailAttestationPresence(
				this.formationSession.props._id,
				attestationId
			);

			await this.attestationPresenceTable.refresh();
			this.createMessageConfirmationSend();
		} catch (error) {
			this.createMessageErrorSend();
			this.errorService.handle(error);
		}
	}

	/**
	 * Handle sending all attestation presence documents
	 * This method collects attestation IDs from the table component
	 * and sends them one by one
	 */
	async handleSendAllAttestationPresence() {
		try {
			// Get all attestations from the table that have generated documents
			const attestations =
				this.attestationPresenceTable.displayItems.filter((item) => {
					if (!item.document || item.isPartial) return false;
					const status =
						this.attestationPresenceTable.getDocumentStatus(item);
					return (
						(status === 'generated' || status === 'created') &&
						item._id
					);
				});

			if (attestations.length === 0) {
				return;
			}

			// Process all attestations one by one
			let successCount = 0;
			let errorCount = 0;

			await Promise.all(
				attestations.map(async (attestation) => {
					try {
						await this.formationSessionService.sendEmailAttestationPresence(
							this.formationSession.props._id,
							attestation._id
						);
						successCount++;
					} catch (error) {
						console.error(
							`Error sending email for attestation ${attestation._id}:`,
							error
						);
						errorCount++;
					}
				})
			);

			// Refresh the table regardless of success/failure
			await this.attestationPresenceTable.refresh();

			// Show appropriate message based on results
			if (errorCount === 0) {
				// All successful
				this.messageConfirmation.success(
					this.translate.instant('common_document_send_all_success', {
						count: successCount,
					})
				);
			} else if (successCount === 0) {
				// All failed
				this.messageConfirmation.error(
					this.translate.instant('common_document_send_all_error')
				);
				this.errorService.handle(
					new Error(`Failed to send ${errorCount} emails`)
				);
			} else {
				// Mixed results
				this.messageConfirmation.warning(
					this.translate.instant('common_document_send_all_partial', {
						success: successCount,
						error: errorCount,
					})
				);
			}
		} catch (error) {
			this.createMessageErrorSend();
			this.errorService.handle(error);
		}
	}

	/**
	 * Handle bulk generate for selected attestations
	 */
	async handleBulkGenerate(event: {
		selectedIds: string[];
		partialRespondantIds?: string[];
		template: string;
		totalTimeOverride?: number;
		respondantTimeOverrides?: { [respondantId: string]: number };
	}): Promise<void> {
		try {
			this.attestationPresenceLoading = true;

			// Check if we have any selections at all
			const hasRealAttestations =
				event.selectedIds && event.selectedIds.length > 0;
			const hasPartialRespondants =
				event.partialRespondantIds &&
				event.partialRespondantIds.length > 0;

			if (!hasRealAttestations && !hasPartialRespondants) {
				// Generate for all respondants if no selection
				await this.handleGenerate(
					{
						template: event.template,
						totalTimeOverride: event.totalTimeOverride,
						respondantTimeOverrides: event.respondantTimeOverrides,
					},
					'attestation_presence'
				);
				return;
			}

			const allPromises: Promise<any>[] = [];
			const respondantTimeOverrides = event.respondantTimeOverrides || {};

			// Handle real attestations (regenerate existing ones)
			if (hasRealAttestations) {
				const selectedRespondants = event.selectedIds
					.map((id) => {
						const displayItem =
							this.attestationPresenceTable.displayItems.find(
								(item) => item._id === id
							);
						if (displayItem && displayItem.owner) {
							if (typeof displayItem.owner === 'string') {
								return this.respondants.find(
									(r) => r.props._id === displayItem.owner
								);
							} else {
								return displayItem.owner;
							}
						}
						return null;
					})
					.filter((r) => r !== null);

				// Add promises for real attestations
				selectedRespondants.forEach((respondant) => {
					const timeOverride =
						respondantTimeOverrides[respondant.props._id];
					allPromises.push(
						this.attestationPresenceService.generateAttestationPresence(
							this.formationSession.props._id,
							respondant.props._id,
							event.template,
							timeOverride?.toString()
						)
					);
				});
			}

			// Handle partial respondants (create new attestations)
			if (hasPartialRespondants) {
				event.partialRespondantIds.forEach((respondantId) => {
					const timeOverride = respondantTimeOverrides[respondantId];
					allPromises.push(
						this.attestationPresenceService.generateAttestationPresence(
							this.formationSession.props._id,
							respondantId,
							event.template,
							timeOverride?.toString()
						)
					);
				});
			}

			// Execute all promises
			await Promise.all(allPromises);

			// Show success message
			const totalGenerated = allPromises.length;
			this.messageConfirmation.success(
				this.translate.instant('common_success_generate') +
					` (${totalGenerated})`
			);
		} catch (error) {
			console.error('Error generating bulk attestations:', error);
		} finally {
			this.attestationPresenceLoading = false;
			if (this.attestationPresenceTable) {
				this.attestationPresenceTable.refresh();
			}
		}
	}

	/**
	 * Handle bulk send for selected attestations
	 */
	async handleBulkSend(selectedIds: string[]): Promise<void> {
		try {
			let successCount = 0;
			let errorCount = 0;

			await Promise.all(
				selectedIds.map(async (id) => {
					try {
						await this.formationSessionService.sendEmailAttestationPresence(
							this.formationSession.props._id,
							id
						);
						successCount++;
					} catch (error) {
						console.error(
							`Error sending email for attestation ${id}:`,
							error
						);
						errorCount++;
					}
				})
			);

			// Refresh the table
			await this.attestationPresenceTable.refresh();

			// Show appropriate message based on results
			if (errorCount === 0) {
				this.messageConfirmation.success(
					this.translate.instant(
						'common_document_send_selected_success',
						{
							count: successCount,
						}
					)
				);
			} else if (successCount === 0) {
				this.messageConfirmation.error(
					this.translate.instant(
						'common_document_send_selected_error'
					)
				);
			} else {
				this.messageConfirmation.warning(
					this.translate.instant(
						'common_document_send_selected_partial',
						{
							success: successCount,
							error: errorCount,
						}
					)
				);
			}
		} catch (error) {
			this.createMessageErrorSend();
			this.errorService.handle(error);
		}
	}

	/**
	 * Handle bulk delete for selected attestations
	 */
	async handleBulkDelete(selectedIds: string[]): Promise<void> {
		this.modalService.confirm({
			nzTitle: this.translate.instant(
				'common_modal_delete_documents_title'
			),
			nzContent: this.translate.instant(
				'common_modal_delete_documents_content',
				{
					count: selectedIds.length,
				}
			),
			nzOkText: this.translate.instant('modal_delete_document_ok'),
			nzOkType: 'danger',
			nzOnOk: () => this.executeBulkDelete(selectedIds),
			nzCancelText: this.translate.instant(
				'modal_delete_document_cancel'
			),
		});
	}

	/**
	 * Execute bulk delete after confirmation
	 */
	private async executeBulkDelete(selectedIds: string[]): Promise<void> {
		try {
			let successCount = 0;
			let errorCount = 0;

			await Promise.all(
				selectedIds.map(async (id) => {
					try {
						await this.attestationPresenceService.removeAsAdmin(id);
						successCount++;
					} catch (error) {
						console.error(
							`Error deleting attestation ${id}:`,
							error
						);
						errorCount++;
					}
				})
			);

			// Refresh the table
			if (this.attestationPresenceTable) {
				this.attestationPresenceTable.refresh();
			}

			// Show appropriate message based on results
			if (errorCount === 0) {
				this.messageConfirmation.success(
					this.translate.instant(
						'common_document_delete_selected_success',
						{
							count: successCount,
						}
					)
				);
			} else if (successCount === 0) {
				this.messageConfirmation.error(
					this.translate.instant(
						'common_document_delete_selected_error'
					)
				);
			} else {
				this.messageConfirmation.warning(
					this.translate.instant(
						'common_document_delete_selected_partial',
						{
							success: successCount,
							error: errorCount,
						}
					)
				);
			}
		} catch (error) {
			console.error('Error executing bulk delete:', error);
			this.messageConfirmation.error(
				this.translate.instant('common_document_delete_selected_error')
			);
		}
	}

	/**
	 * Handle bulk download for selected attestations
	 */
	async handleBulkDownloadAttestationPresence(
		downloadItems: { attestationId: string; filename: string }[]
	): Promise<void> {
		if (!downloadItems || downloadItems.length === 0) return;

		try {
			// Process downloads in parallel
			await Promise.all(
				downloadItems.map((item) =>
					this.handleDownloadDocumentAttestationPresence(item)
				)
			);
		} catch (error) {
			console.error('Error in bulk download:', error);
			this.errorService.handle(error);
		}
	}

	showDeleteConfirmOnAttestationPresence(id: string): void {
		this.modalService.confirm({
			nzTitle: this.translate.instant('modal_delete_document_title'),
			nzContent: this.translate.instant('modal_delete_document_content'),
			nzOkText: this.translate.instant('modal_delete_document_ok'),
			nzOkType: 'danger',
			nzOnOk: () => this.deleteAttestationPresence(id),
			nzCancelText: this.translate.instant(
				'modal_delete_document_cancel'
			),
		});
	}

	async deleteAttestationPresence(id: string): Promise<void> {
		try {
			// Implement delete logic
			await this.attestationPresenceService.removeAsAdmin(id);
			if (this.attestationPresenceTable) {
				this.attestationPresenceTable.refresh();
			}
		} catch (error) {
			console.error('Error deleting attestation presence:', error);
		}
	}

	private downloadPdf(url: string, filename: string) {
		this.http
			.get(url, { responseType: 'blob' })
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((response) => {
				const file = new Blob([response], { type: response.type });

				const fileURL = URL.createObjectURL(file);
				const fakeDownload = document.createElement('a');
				fakeDownload.href = fileURL;
				fakeDownload.target = '_blank';
				fakeDownload.download = filename;
				document.body.appendChild(fakeDownload);
				fakeDownload.click();
				URL.revokeObjectURL(fileURL);
				fakeDownload.remove();
			});
	}

	private createMessageConfirmationSend(): void {
		this.messageConfirmation.success(
			this.translate.instant('common_document_send')
		);
	}

	private createMessageErrorSend(): void {
		this.messageConfirmation.error(
			this.translate.instant('common_document_send_error')
		);
	}
}
